import React, { useEffect, useState } from "react";
import { Save, KeyRound, Send, Loader2, CheckCircle2, AlertTriangle, Eye, EyeOff } from "lucide-react";
import { adminApi } from "../lib/api";

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [resendTesting, setResendTesting] = useState(false);
  const [resendResult, setResendResult] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Form state (secrets stay empty unless user types — PATCH only non-empty)
  const [form, setForm] = useState({
    pawapay_sandbox_token: "",
    pawapay_production_token: "",
    pawapay_env: "sandbox",
    resend_api_key: "",
    resend_from_email: "",
  });
  const [showSecrets, setShowSecrets] = useState({ sandbox: false, prod: false, resend: false });

  const load = () => {
    setLoading(true);
    adminApi.getSettings()
      .then(({ data }) => {
        setSettings(data);
        setForm((f) => ({ ...f, pawapay_env: data.pawapay_env || "sandbox", resend_from_email: data.resend_from_email || "" }));
      })
      .catch((e) => setError(e?.response?.data?.detail || "Impossible de charger les paramètres"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setError(""); setSuccess("");
    // Only send non-empty string fields; env always sent
    const patch = { pawapay_env: form.pawapay_env };
    for (const k of ["pawapay_sandbox_token", "pawapay_production_token", "resend_api_key", "resend_from_email"]) {
      if (form[k] && form[k].trim()) patch[k] = form[k].trim();
    }
    try {
      const { data } = await adminApi.updateSettings(patch);
      setSettings(data);
      setSuccess("Paramètres enregistrés.");
      // Clear secret inputs after save
      setForm((f) => ({ ...f, pawapay_sandbox_token: "", pawapay_production_token: "", resend_api_key: "" }));
      setTimeout(() => setSuccess(""), 4000);
    } catch (err) {
      setError(err?.response?.data?.detail || "Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const testPawa = async () => {
    setTesting(true); setTestResult(null);
    try {
      const { data } = await adminApi.testPawapay();
      setTestResult(data);
    } catch (err) {
      setTestResult({ ok: false, detail: err?.response?.data?.detail || "Erreur réseau" });
    } finally {
      setTesting(false);
    }
  };

  const testResend = async () => {
    setResendTesting(true); setResendResult(null);
    try {
      const { data } = await adminApi.testResend("");
      setResendResult(data);
    } catch (err) {
      setResendResult({ sent: false, reason: err?.response?.data?.detail || "Erreur réseau" });
    } finally {
      setResendTesting(false);
    }
  };

  if (loading) return <div className="text-sm text-foreground/50">Chargement…</div>;

  const s = settings || {};

  return (
    <div data-testid="admin-settings-page">
      <div className="mb-8">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent mb-2">
          / paramètres plateforme
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-semibold text-primary tracking-tight">
          Paramètres
        </h1>
        <p className="text-sm text-foreground/60 mt-2 max-w-2xl">
          Les tokens saisis ici remplacent ceux du fichier <code className="font-mono text-xs">.env</code>.
          Ils sont stockés en base et masqués après enregistrement.
        </p>
      </div>

      {error && (
        <div className="mb-4 text-sm text-[#991B1B] bg-[#FEE2E2] border border-[#FECACA] px-3 py-2 rounded-sm" data-testid="settings-error">
          <AlertTriangle size={14} className="inline mr-1 -mt-0.5" />{error}
        </div>
      )}
      {success && (
        <div className="mb-4 text-sm text-[#065F46] bg-[#D1FAE5] border border-[#A7F3D0] px-3 py-2 rounded-sm" data-testid="settings-success">
          <CheckCircle2 size={14} className="inline mr-1 -mt-0.5" />{success}
        </div>
      )}

      <form onSubmit={save} className="space-y-8 max-w-3xl">
        {/* PawaPay */}
        <section className="bg-white border border-primary/15 rounded-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-accent mb-1">/ paiement</div>
              <h2 className="font-display text-xl font-semibold text-primary">PawaPay Mobile Money</h2>
            </div>
            <button
              type="button"
              onClick={testPawa}
              disabled={testing}
              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 border border-primary/20 text-primary rounded-sm hover:bg-primary/5 transition-colors disabled:opacity-50"
              data-testid="settings-test-pawapay-btn"
            >
              {testing ? <Loader2 size={13} className="animate-spin" /> : <KeyRound size={13} />} Tester la connexion
            </button>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <Field
              label="Environnement"
              testid="settings-pawapay-env"
              input={
                <select
                  value={form.pawapay_env}
                  onChange={(e) => setForm({ ...form, pawapay_env: e.target.value })}
                  className="w-full bg-transparent text-sm py-2 outline-none"
                >
                  <option value="sandbox">Sandbox</option>
                  <option value="production">Production</option>
                </select>
              }
            />
            <div />
            <SecretField
              label="Sandbox token"
              placeholder={s.pawapay_sandbox_token?.masked || "Collez le token sandbox…"}
              source={s.pawapay_sandbox_token?.source}
              value={form.pawapay_sandbox_token}
              onChange={(v) => setForm({ ...form, pawapay_sandbox_token: v })}
              show={showSecrets.sandbox}
              onToggle={() => setShowSecrets({ ...showSecrets, sandbox: !showSecrets.sandbox })}
              testid="settings-pawapay-sandbox"
            />
            <SecretField
              label="Production token"
              placeholder={s.pawapay_production_token?.masked || "Collez le token production…"}
              source={s.pawapay_production_token?.source}
              value={form.pawapay_production_token}
              onChange={(v) => setForm({ ...form, pawapay_production_token: v })}
              show={showSecrets.prod}
              onToggle={() => setShowSecrets({ ...showSecrets, prod: !showSecrets.prod })}
              testid="settings-pawapay-production"
            />
          </div>

          {testResult && (
            <div
              className={`mt-4 text-xs px-3 py-2 rounded-sm font-mono ${
                testResult.ok
                  ? "bg-[#D1FAE5] text-[#065F46] border border-[#A7F3D0]"
                  : "bg-[#FEE2E2] text-[#991B1B] border border-[#FECACA]"
              }`}
              data-testid="settings-test-pawapay-result"
            >
              {testResult.ok ? "✓ Connexion PawaPay OK" : "✗ Connexion échouée"} · env={testResult.env}
              {testResult.http_status ? ` · http=${testResult.http_status}` : ""}
              {testResult.detail ? ` · ${String(testResult.detail).slice(0, 200)}` : ""}
            </div>
          )}
          {testResult && testResult.demo && (
            <div className="mt-2 text-[11px] text-foreground/50 font-mono">
              Aucun token actif — le système fonctionne en mode démo.
            </div>
          )}
        </section>

        {/* Resend */}
        <section className="bg-white border border-primary/15 rounded-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-accent mb-1">/ emails</div>
              <h2 className="font-display text-xl font-semibold text-primary">Resend (emails transactionnels)</h2>
            </div>
            <button
              type="button"
              onClick={testResend}
              disabled={resendTesting}
              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 border border-primary/20 text-primary rounded-sm hover:bg-primary/5 transition-colors disabled:opacity-50"
              data-testid="settings-test-resend-btn"
            >
              {resendTesting ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Envoyer un test
            </button>
          </div>
          <p className="text-xs text-foreground/60 mb-4">
            Obtenez une clé API sur <a className="underline" href="https://resend.com" target="_blank" rel="noreferrer">resend.com</a>.
            Le test envoie un email à votre adresse de récupération.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <SecretField
              label="Clé API Resend"
              placeholder={s.resend_api_key?.masked || "Collez re_xxx…"}
              source={s.resend_api_key?.source}
              value={form.resend_api_key}
              onChange={(v) => setForm({ ...form, resend_api_key: v })}
              show={showSecrets.resend}
              onToggle={() => setShowSecrets({ ...showSecrets, resend: !showSecrets.resend })}
              testid="settings-resend-apikey"
            />
            <Field
              label="Adresse d'expédition (From)"
              testid="settings-resend-from"
              input={
                <input
                  type="text"
                  value={form.resend_from_email}
                  onChange={(e) => setForm({ ...form, resend_from_email: e.target.value })}
                  placeholder="Lexihab <no-reply@votre-domaine.cm>"
                  className="w-full bg-transparent text-sm py-2 outline-none"
                />
              }
            />
          </div>
          {resendResult && (
            <div
              className={`mt-4 text-xs px-3 py-2 rounded-sm font-mono ${
                resendResult.sent
                  ? "bg-[#D1FAE5] text-[#065F46] border border-[#A7F3D0]"
                  : "bg-[#FEF3C7] text-[#92400E] border border-[#FDE68A]"
              }`}
              data-testid="settings-test-resend-result"
            >
              {resendResult.sent ? `✓ Email envoyé (id=${resendResult.id || "ok"})` : `✗ ${resendResult.reason || "échec"}`}
              {resendResult.dev_reset_url && (
                <div className="mt-1 break-all">URL de test dev : <a className="underline" href={resendResult.dev_reset_url}>{resendResult.dev_reset_url}</a></div>
              )}
            </div>
          )}
        </section>

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-sm text-sm font-medium hover:bg-primary-light transition-colors disabled:opacity-60"
            data-testid="settings-save-btn"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Enregistrer
          </button>
          <span className="text-[11px] font-mono text-foreground/40">
            {s.updated_at ? `Dernière mise à jour : ${new Date(s.updated_at).toLocaleString()}${s.updated_by ? ` par ${s.updated_by}` : ""}` : ""}
          </span>
        </div>
      </form>
    </div>
  );
}

function Field({ label, input, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-foreground/60 mb-1.5">{label}</span>
      <div className="border border-primary/20 rounded-sm px-3 focus-within:border-primary transition-colors bg-white">
        {input}
      </div>
    </label>
  );
}

function SecretField({ label, placeholder, source, value, onChange, show, onToggle, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-foreground/60">{label}</span>
        {source && source !== "none" && (
          <span className={`font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${source === "db" ? "bg-primary/10 text-primary" : "bg-[#FEF3C7] text-[#92400E]"}`}>
            {source === "db" ? "en base" : "env"}
          </span>
        )}
      </div>
      <div className="flex items-center gap-1 border border-primary/20 rounded-sm px-3 focus-within:border-primary transition-colors bg-white">
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="flex-1 bg-transparent text-sm py-2 outline-none font-mono"
          autoComplete="new-password"
        />
        <button type="button" onClick={onToggle} className="text-foreground/40 hover:text-primary p-1">
          {show ? <EyeOff size={14} /> : <Eye size={14} />}
        </button>
      </div>
    </label>
  );
}
